import React, {useState} from 'react';
import {Grid, Header} from 'semantic-ui-react';
import PropTypes from 'prop-types';
import {orderBy as _orderBy} from 'lodash';

import {hasPrivileges} from '@appComponents/Authorization';
import Divider from '@ui/Divider';
import {showModal, showModalConfirmation} from '@utils/modal';
import {convertToInt} from '@utils/helpers';
import {IconDelete} from '@appComponents/IconCollection';
import {THIRD_PARTY_CLIENT_TUNNEL} from '@constants/resources';
import {SECURITY_PRIVILEGES_READ, SECURITY_PRIVILEGES_UPDATE} from '@constants/variables';
import ClientOutputClientReferenceForm
    from '@modules/client/forms/ClientPackageTunnels/OutputClientReferences/ClientOutputClientReferenceForm';
import HiddenReferencesField
    from '@modules/client/forms/ClientPackageTunnels/OutputClientReferences/HiddenReferencesField';
import ClientTunnelModalContent
    from '@modules/client/forms/ClientPackageTunnels/OutputClientReferences/ClientTunnelModalContent';

const ClientOutputClientReferenceTable = ({props, variables, formData}) => {
    const [references, setReferences] = useState([]);
    const [referencesSave, setReferencesSave] = useState(false);
    const [clientReferencesReady, setClientReferencesReady] = useState(false);
    const [activeEditableFields, setActiveEditableFields] = useState(null);
    const authorization = {
        update: hasPrivileges({[THIRD_PARTY_CLIENT_TUNNEL]: SECURITY_PRIVILEGES_UPDATE}),
        add: hasPrivileges({[THIRD_PARTY_CLIENT_TUNNEL]: SECURITY_PRIVILEGES_READ}),
    };

    let clientReferences = _orderBy([
        ...references,
    ], ['output_reference'], ['asc']);

    if (!variables.rerender && props?.data?.thirdPartyClientTunnel?.output_references && !referencesSave) {

        let clientReferences = _orderBy([
            ...references,
            ...(props?.data?.thirdPartyClientTunnel?.output_references || []),
        ], ['output_reference'], ['asc']);

        setReferences(clientReferences);
        setReferencesSave(true);
    }

    if (variables.rerender) {
        if (!clientReferencesReady) {
            clientReferences = _orderBy([
                ...(formData?.output_client_references || []),
            ], ['output_reference'], ['asc']);

            setReferences(clientReferences);
            setClientReferencesReady(true);
        }
    }

    const deleteReference = (indexId) => {
        const updatedReferences = clientReferences.filter((domain, index) => index !== convertToInt(indexId));
        setReferences(updatedReferences);

        return updatedReferences;
    };

    const reRenderModal = (props, vars, references) => {
        let variables;

        if (!vars?.id) {
            variables = {...vars, rerender: true};
        } else {
            variables = {id: parseInt(vars.id), ...vars, rerender: true};
        }


        showModal({
            size: 'large',
            canBeDismissed: false,
            isVisible: true,
            header: null,
            content: <ClientTunnelModalContent variables={variables} props={props} formParams={{
                ...props.formValues,
                output_client_references: references,
            }}/>,
        });
    };

    const removeReference = (index, props, variables) => {
        showModalConfirmation({
            header: <Header icon='trash' content={'Delete reference'}/>,
            text: 'Are you sure you want to remove this reference?',
            customStyle: {width: '500px'},
            onYes: () => {
                reRenderModal(props, variables, deleteReference(index));
            },
            onNo: () => {
                reRenderModal(props, variables, references);
            },
        });
    };

    const renderButton = (data, index, props, variables) => {
        return (
            <div className='--flex actions-list-icon --custom-gap --flex-row-reverse'>
                <IconDelete
                    icon='trash'
                    size='large'
                    onClick={() => authorization.update ? removeReference(index, props, variables) : null}
                />
            </div>
        );
    };

    const renderEditableGrids = (props, variables) => {
        return clientReferences.map((data, index) => {
            return (
                <React.Fragment key={index}>
                    {(activeEditableFields === convertToInt(index))
                        ? renderEditForm(data, index, 'form_cvd_edit', variables)
                        : renderEditableGrid(data, index, props, variables)}
                </React.Fragment>
            );
        });
    };

    const renderEditableGrid = (data, index, props, variables) => {
        const onClickCallback = authorization.update ? activateEditableCell : null;

        return (
            <Grid key={index} verticalAlign='top'>
                <Grid.Row data-id={index} onClick={onClickCallback} className='editableTable__trigger'>
                    <Grid.Column width={2}></Grid.Column>
                    <Grid.Column width={6} className='reference-column --medium-width'>
                        {data.output_reference}
                    </Grid.Column>
                    <Grid.Column width={6} className='description-column'>
                        {data.description}
                    </Grid.Column>
                    <Grid.Column width={2}>
                        {renderButton(data, index, props, variables)}
                    </Grid.Column>
                </Grid.Row>
                <Divider sx={{
                    marginTop: 0,
                    flexGrow: 1,
                }}/>
            </Grid>
        );
    };

    const activateEditableCell = (event) => {
        setActiveEditableFields(convertToInt(event.currentTarget.dataset.id));
    };

    const handleDeactivateEditableCell = () => {
        setActiveEditableFields(null);
    };

    const renderAddForm = (variables) => (
        <ClientOutputClientReferenceForm
            formType='form_ref_add'
            references={references}
            setReferences={setReferences}
            variables={variables}
        />
    );

    const renderEditForm = (data, key, formType, variables) => {
        return (
            <ClientOutputClientReferenceForm
                formType={formType}
                formData={{
                    id: key,
                    fields: data,
                }}
                references={clientReferences}
                setReferences={setReferences}
                onDeactivateEditableCell={handleDeactivateEditableCell}
                variables={variables}
            />
        );
    };

    return (
        <div className='--form-container --align-output-client-references'>
            {HiddenReferencesField(references)}
            {renderAddForm(variables)}
            {renderEditableGrids(props, variables)}
        </div>
    );
};

ClientOutputClientReferenceTable.displayName = 'ClientOutputClientReferenceTable';

ClientOutputClientReferenceTable.propTypes = {
    data: PropTypes.object,
    props: PropTypes.object,
    formValues: PropTypes.object,
    variables: PropTypes.object,
    formData: PropTypes.object,
};

export default ClientOutputClientReferenceTable;
